import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const FinanceContext = createContext();

export const useFinance = () => useContext(FinanceContext);

export const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const FinanceProvider = ({ children }) => {
  // Start with false — no spinner until a fetch actually begins
  const [months, setMonths] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthLoading, setMonthLoading] = useState(false);

  const [reportsData, setReportsData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);

  // Keep a stable ref to the current month to avoid stale closures in async functions
  const currentMonthRef = useRef(null);
  useEffect(() => {
    currentMonthRef.current = currentMonth;
  }, [currentMonth]);

  // Simple cache: stores the last fetched data keyed by a string
  const monthsCacheRef = useRef(null);        // null = not fetched yet
  const monthDetailsCacheRef = useRef({});    // { [id]: data }
  const reportsCacheRef = useRef(null);

  // ─── Months ────────────────────────────────────────────────────────────────

  const fetchRecentMonths = useCallback(async (force = false) => {
    // Return cached data immediately to avoid unnecessary network calls
    if (!force && monthsCacheRef.current !== null) {
      setMonths(monthsCacheRef.current);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/months');
      monthsCacheRef.current = res.data;
      setMonths(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);
  // ─── Month Details ─────────────────────────────────────────────────────────

  const fetchMonthDetails = useCallback(async (id, force = false) => {
    // Serve cached month immediately for instant render
    if (!force && monthDetailsCacheRef.current[id]) {
      setCurrentMonth(monthDetailsCacheRef.current[id]);
      setMonthLoading(false);
      return;
    }
    setMonthLoading(true);
    try {
      const res = await api.get(`/months/${id}?_t=${Date.now()}`);
      monthDetailsCacheRef.current[id] = res.data;
      setCurrentMonth(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setMonthLoading(false);
    }
  }, []);


  const createMonth = async (data) => {
    const res = await api.post('/months', data);
    // Bust the cache so the list refreshes next visit
    monthsCacheRef.current = null;
    return res.data;
  };

  const updateMonth = useCallback(async (id, data) => {
    // Optimistic update for All Months list
    setMonths(prev =>
      prev.map(m => String(m.id) === String(id) ? { ...m, ...data } : m)
    );
    if (monthsCacheRef.current) {
      monthsCacheRef.current = monthsCacheRef.current.map(m =>
        String(m.id) === String(id) ? { ...m, ...data } : m
      );
    }

    try {
      await api.put(`/months/${id}`, data);
      
      // If we are updating the currently viewed month, trigger a refresh
      const mId = currentMonthRef.current?.month?.id;
      if (String(mId) === String(id)) {
        fetchMonthDetails(id, true);
      }
    } catch (e) {
      // Rollback on failure by re-fetching
      console.error(e);
      monthsCacheRef.current = null;
      fetchRecentMonths(true);
      
      const mId = currentMonthRef.current?.month?.id;
      if (String(mId) === String(id)) {
        fetchMonthDetails(id, true);
      }
    }
  }, [fetchRecentMonths, fetchMonthDetails]);

  const deleteMonth = useCallback(async (id) => {
    // Optimistic removal
    setMonths(prev => prev.filter(m => m.id !== id));
    if (monthsCacheRef.current) {
      monthsCacheRef.current = monthsCacheRef.current.filter(m => m.id !== id);
    }
    delete monthDetailsCacheRef.current[id];
    try {
      await api.delete(`/months/${id}`);
    } catch (e) {
      console.error(e);
      monthsCacheRef.current = null;
      fetchRecentMonths(true);
    }
  }, [fetchRecentMonths]);


  // Helper: update currentMonth state + cache optimistically
  const _optimisticMonthUpdate = (updater) => {
    setCurrentMonth(prev => {
      if (!prev) return prev;
      const next = updater(prev);
      
      // Auto-recalculate summary for dynamic UI updates
      const income = parseFloat(next.month?.expected_income || 0);
      const rent = next.month?.paysRent ? parseFloat(next.month?.rent_amount || 0) : 0;
      const total_debt = (next.debts || []).reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
      const total_budgeted = (next.budgets || []).reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
      
      next.summary = {
        ...next.summary,
        expected_income: income,
        rent: rent,
        total_debt: total_debt,
        total_budgeted: total_budgeted,
        remaining: income - rent - total_debt - total_budgeted
      };
      
      if (prev.month?.id) {
        monthDetailsCacheRef.current[prev.month.id] = next;
      }
      return next;
    });
  };

  // ─── Budgets ───────────────────────────────────────────────────────────────

  const addBudget = async (data) => {
    const res = await api.post('/budgets', data);
    const newBudget = { ...data, id: res.data.id ?? Date.now(), spent: 0 };
    _optimisticMonthUpdate(prev => ({
      ...prev,
      budgets: [...(prev.budgets || []), newBudget],
    }));
    const mId = currentMonthRef.current?.month?.id;
    if (mId) fetchMonthDetails(mId, true);
  };

  const addMultipleBudgets = async (items) => {
    const results = await Promise.all(items.map(data => api.post('/budgets', data)));
    const newBudgets = items.map((data, i) => ({
      ...data, id: results[i].data.id ?? Date.now() + i, spent: 0
    }));
    _optimisticMonthUpdate(prev => ({
      ...prev,
      budgets: [...(prev.budgets || []), ...newBudgets],
    }));
    const mId = currentMonthRef.current?.month?.id;
    if (mId) fetchMonthDetails(mId, true);
  };

  const updateBudget = async (id, data) => {
    _optimisticMonthUpdate(prev => ({
      ...prev,
      budgets: prev.budgets.map(b => String(b.id) === String(id) ? { ...b, ...data } : b),
    }));
    try {
      await api.put(`/budgets/${id}`, data);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    } catch (e) {
      console.error(e);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    }
  };

  const deleteBudget = async (id) => {
    _optimisticMonthUpdate(prev => ({
      ...prev,
      budgets: prev.budgets.filter(b => String(b.id) !== String(id)),
    }));
    try {
      await api.delete(`/budgets/${id}`);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    } catch (e) {
      console.error(e);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    }
  };

  // ─── Debts ─────────────────────────────────────────────────────────────────

  const addDebt = async (data) => {
    const res = await api.post('/debts', data);
    const newDebt = { ...data, id: res.data.id ?? Date.now(), status: 'Unpaid' };
    _optimisticMonthUpdate(prev => ({
      ...prev,
      debts: [...(prev.debts || []), newDebt],
    }));
    const mId = currentMonthRef.current?.month?.id;
    if (mId) fetchMonthDetails(mId, true);
  };

  const addMultipleDebts = async (items) => {
    const results = await Promise.all(items.map(data => api.post('/debts', data)));
    const newDebts = items.map((data, i) => ({
      ...data, id: results[i].data.id ?? Date.now() + i, status: 'Unpaid'
    }));
    _optimisticMonthUpdate(prev => ({
      ...prev,
      debts: [...(prev.debts || []), ...newDebts],
    }));
    const mId = currentMonthRef.current?.month?.id;
    if (mId) fetchMonthDetails(mId, true);
  };

  const updateDebt = async (id, data) => {
    _optimisticMonthUpdate(prev => ({
      ...prev,
      debts: prev.debts.map(d => String(d.id) === String(id) ? { ...d, ...data } : d),
    }));
    try {
      await api.put(`/debts/${id}`, data);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    } catch (e) {
      console.error(e);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    }
  };

  const deleteDebt = async (id) => {
    _optimisticMonthUpdate(prev => ({
      ...prev,
      debts: prev.debts.filter(d => String(d.id) !== String(id)),
    }));
    try {
      await api.delete(`/debts/${id}`);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    } catch (e) {
      console.error(e);
      const mId = currentMonthRef.current?.month?.id;
      if (mId) fetchMonthDetails(mId, true);
    }
  };

  // ─── Reports ───────────────────────────────────────────────────────────────

  const fetchReports = useCallback(async (force = false) => {
    if (!force && reportsCacheRef.current !== null) {
      setReportsData(reportsCacheRef.current);
      return;
    }
    setReportsLoading(true);
    try {
      const res = await api.get('/reports');
      reportsCacheRef.current = res.data;
      setReportsData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // ─── Sticky Items ──────────────────────────────────────────────────────────

  const [stickyItems, setStickyItems] = useState([]);
  const [stickyLoading, setStickyLoading] = useState(false);
  const stickyFetchedRef = useRef(false);

  const fetchStickyItems = useCallback(async (force = false) => {
    if (!force && stickyFetchedRef.current) return;
    setStickyLoading(true);
    try {
      const res = await api.get('/sticky_items');
      stickyFetchedRef.current = true;
      setStickyItems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setStickyLoading(false);
    }
  }, []);

  const addStickyItem = async (data) => {
    const res = await api.post('/sticky_items', data);
    const newItem = { ...data, id: res.data.id ?? Date.now() };
    setStickyItems(prev => [...prev, newItem]);
  };

  const updateStickyItem = async (id, data) => {
    setStickyItems(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
    try {
      await api.put(`/sticky_items/${id}`, data);
    } catch (e) {
      console.error(e);
      fetchStickyItems(true);
    }
  };

  const deleteStickyItem = async (id) => {
    setStickyItems(prev => prev.filter(i => i.id !== id));
    try {
      await api.delete(`/sticky_items/${id}`);
    } catch (e) {
      console.error(e);
      fetchStickyItems(true);
    }
  };

  // ─── Context Value ─────────────────────────────────────────────────────────

  const value = {
    months,
    currentMonth,
    fetchRecentMonths,
    createMonth,
    fetchMonthDetails,
    updateMonth,
    deleteMonth,
    addBudget,
    addMultipleBudgets,
    updateBudget,
    deleteBudget,
    addDebt,
    addMultipleDebts,
    updateDebt,
    deleteDebt,
    loading,
    monthLoading,
    reportsData,
    reportsLoading,
    fetchReports,
    stickyItems,
    stickyLoading,
    fetchStickyItems,
    addStickyItem,
    updateStickyItem,
    deleteStickyItem,
  };

  return (
    <FinanceContext.Provider value={value}>
      {children}
    </FinanceContext.Provider>
  );
};
